import { RmsAccountBadgeColumns } from '@/backend/features/badging/badging-customizations/shared';
import { ExtendedAccountColumns } from '@/backend/features/contact-management/entities/extended-account/types';
import { createBadgeFunctionDef } from '@juvare/badging/backend/features/badges/platform-functions/createBadgesSchema';
import {
    BadgeCreateComponentDuplicateProps,
    BadgeCreateComponentProps,
} from '@juvare/badging/ui/features/badge-management/components/BadgeCreate/BadgeCreateComponentType';
import { manageBadgesRoutes } from '@juvare/badging/ui/features/badge-management/utils/manageBadgesRoutes';
import { FullScreenWizard } from '@juvare/common-form-ui/FullScreenWizard';
import { WizardStep } from '@juvare/common-form-ui/Wizard';
import { showInfoSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { requireAnySystemPermission } from '@juvare/contact-management/backend/features/permissions/shared';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { useTypedPlatformFunction } from '@juvare/platform-sdk-react/ui/features/typed-platform-functions';
import { Row } from '@juvare/platform-shared/features/storage';
import { Box } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router';
import { RmsBadgeCreateWizardStep1 } from './RmsBadgeCreateWizardStep1';
import { RmsBadgeCreateWizardStep2 } from './RmsBadgeCreateWizardStep2';
import { RmsBadgeCreateWizardStep3 } from './RmsBadgeCreateWizardStep3';
import { BadgeCreateWizardData } from './types';

export const RmsBadgeCreate: React.FC<BadgeCreateComponentProps> = (props) => {
    const { t } = useTranslation('rms');
    const userState = useUserState();
    const navigate = useNavigate();

    const wizardConfig = useBadgeCreateWizard(
        'badgeToDuplicate' in props ? duplicateBadgeData(props) : { badgeTemplate: props.badgeTemplate },
    );

    const createBadgePlatformFn = useTypedPlatformFunction(createBadgeFunctionDef);

    const handleBadgeCreate = React.useCallback(
        async (data: BadgeCreateWizardData) => {
            await createBadgePlatformFn({
                badgeName: data.badgeDocumentName,
                badgeDescription: data.badgeDescription,
                expirationIntervalMonths: data.formSettings?.defaultExpirationInterval ?? 6,
                badgeTemplate: data.badgeTemplate!,
                formSettings: data.formSettings ?? {},
                organizationsAdminsCanAssignBadges: data.organizationsAdminsCanAssignBadges ?? [],
                accounts: data.accounts,
            }).catch((err) => {
                throw new Error((err as Error | undefined)?.message ?? t('badging.create.saveRequestFailed'));
            });
        },
        [createBadgePlatformFn, t],
    );

    const handleBadgeCreateSuccess = React.useCallback(() => {
        showInfoSnackbar({ message: t('badging.create.wizard.createSuccess') });
        navigate(manageBadgesRoutes.home('manage-badges', { base: '../' }));
    }, [navigate, t]);

    const handleBadgeCreateCancel = React.useCallback(() => {
        navigate(-1);
    }, [navigate]);

    if (!requireAnySystemPermission(userState, 'manage-badges')) {
        return <Navigate to={manageBadgesRoutes.home('manage-badges', { base: '../' })} replace />;
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <FullScreenWizard
                title={'badgeToDuplicate' in props ? t('badging.create.wizard.titleDuplicate') : t('badging.create.wizard.titleCreate')}
                open={true}
                data={wizardConfig.initialData}
                steps={wizardConfig.steps}
                onCancel={handleBadgeCreateCancel}
                submitButtonLabel={t('badging.create.wizard.buttonFinish').toUpperCase()}
                onSubmit={handleBadgeCreate}
                onSubmitSuccess={handleBadgeCreateSuccess}
            />
        </Box>
    );
};

function useBadgeCreateWizard(data: Partial<BadgeCreateWizardData>) {
    const { t } = useTranslation('rms');

    const initialData: BadgeCreateWizardData = React.useMemo(
        () => ({
            badgeTemplate: undefined,
            badgeDocumentName: '',
            badgeDescription: '',
            formSettings: undefined,
            accounts: [],
            ...data,
        }),
        [data],
    );

    const steps: WizardStep[] = React.useMemo(
        () => [
            {
                label: t('badging.create.wizard.step1.progressTitle'),
                render: (props) => <RmsBadgeCreateWizardStep1 {...props} />,
            },
            {
                label: t('badging.create.wizard.step2.progressTitle'),
                render: (props) => <RmsBadgeCreateWizardStep2 {...props} />,
            },
            {
                label: t('badging.create.wizard.step3.progressTitle'),
                render: (props) => <RmsBadgeCreateWizardStep3 {...props} />,
            },
        ],
        [t],
    );

    return {
        steps,
        initialData,
    };
}

function duplicateBadgeData(props: BadgeCreateComponentDuplicateProps): Required<BadgeCreateWizardData> {
    const { badgeTemplate, badgeToDuplicate, accountSelectionsToDuplicate } = props;
    const accounts = accountSelectionsToDuplicate.map(({ id, account, accountBadge, options }) => {
        const rmsAccountBadge = accountBadge as Row<RmsAccountBadgeColumns>;
        return {
            id: id,
            account: account as ExtendedAccountColumns,
            options: {
                ...options,
                occupationId: rmsAccountBadge.columns.occupationId,
            },
        };
    });

    return {
        badgeDocumentName: badgeToDuplicate.data.name,
        badgeDescription: badgeToDuplicate.data.description ?? '',
        badgeTemplate: badgeTemplate,
        formSettings: badgeToDuplicate.data.formSettings,
        organizationsAdminsCanAssignBadges: badgeToDuplicate.data.organizationIds ?? [],
        accounts,
    };
}
